import styles from './ProfilePage.module.css';
import { useUser } from '../../../Context/useUser';
import { useEffect, useState } from 'react';
import API from '../../../Utils/api';

import Avatar from 'react-avatar';
import { useParams } from 'react-router';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import { toast } from 'react-toastify';

import { FaCreditCard, FaPassport } from 'react-icons/fa';
import { FaPersonCircleQuestion, FaLocationDot, FaBuildingColumns } from 'react-icons/fa6';
import { IoNotifications } from 'react-icons/io5';
import { MdOutlineSecurity } from 'react-icons/md';
import { TbChartCandle } from 'react-icons/tb';
import { HiOutlineIdentification } from 'react-icons/hi';
import { BsCashCoin } from 'react-icons/bs';
import { BsBank } from 'react-icons/bs';
import { FaRegCreditCard } from 'react-icons/fa6';

import { Badge, Button, Col, Container, Row } from 'react-bootstrap';
import Accordion from 'react-bootstrap/Accordion';
import EditableField from '../../../Components/EditableField/EditableField';
import EditableTimeField from '../../../Components/EditableTimeField/EditableTimeField';
import Table from '../../../Components/Table/Table';
import { useForm } from 'react-hook-form';
import { getProfileSaveFeedback, stripSetupEmailStatus } from './emailUpdateFeedback';

const targetTimeTransform = obj => {
    let times = 0;
    if (obj?.hours && !isNaN(obj?.hours)) times += 3600 * obj.hours;
    if (obj?.minutes && !isNaN(obj?.minutes)) times += 60 * obj.minutes;
    if (obj?.seconds && !isNaN(obj?.seconds)) times += obj.seconds;
    return times;
};

const userTargetTimeTransform = targetTime => {
    if (!targetTime || typeof targetTime !== 'number') return targetTime;

    const obj = {};

    if (targetTime >= 3600) {
        obj.hours = Math.floor(targetTime / 3600);
        targetTime = targetTime % 3600;
    }

    obj.minutes = Math.floor(targetTime / 60);
    obj.seconds = targetTime % 60;

    return obj;
};

export default function ProfilePage() {
    const { user, setUser } = useUser();
    const { athleteId } = useParams();
    const visitedUser = useVisitingUser(athleteId);
    const [currentUser, setCurrentUser] = useState(null);
    const [athleteStats, setAthleteStats] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isSavingProfile, setIsSavingProfile] = useState(false);

    // TODO: Split these into separate components for each view mode
    const [viewMode, setViewMode] = useState('general'); // 'security', 'billing'?, 'notifications'?

    const [biometrics, setBiometrics] = useState({
        height: currentUser?.height || null,
        weight: currentUser?.weight || null,
        birthYear: currentUser?.birthYear || null
    });

    const { register, handleSubmit, reset } = useForm({
        defaultValues: {
            changesToAccount: user?.notifications?.changesToAccount ?? false,
            promotionalOffers: user?.notifications?.promotionalOffers ?? false
        }
    });

    const {
        register: registerPassword,
        handleSubmit: handlePasswordSubmit,
        reset: resetPasswordForm,
        formState: { errors: passwordErrors, isSubmitting }
    } = useForm();

    const onPasswordChange = async ({ currentPassword, newPassword, confirmPassword }) => {
        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match.');
            return;
        }
        try {
            await API.updatePassword({
                userId: currentUser._id,
                oldPassword: currentPassword,
                newPassword
            });
            toast.success('Password updated successfully.');
            resetPasswordForm();
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Failed to update password.');
        }
    };

    useEffect(() => {
        if (currentUser) {
            reset({
                changesToAccount: currentUser.notifications?.changesToAccount ?? false,
                promotionalOffers: currentUser.notifications?.promotionalOffers ?? false
            });
        }
    }, [currentUser, reset]);

    const onNotificationFormSubmit = data => {
        // Convert checkbox values to booleans
        const updatedNotifications = {
            changesToAccount: !!data.changesToAccount,
            promotionalOffers: !!data.promotionalOffers
        };
        const updatedUser = {
            ...currentUser,
            notifications: {
                ...currentUser.notifications,
                ...updatedNotifications
            }
        };
        queueUserUpdate(updatedUser);
    };

    useEffect(() => {
        setBiometrics({
            height: currentUser?.height || null,
            weight: currentUser?.weight || null,
            birthYear: currentUser?.birthYear || null
        });
    }, [currentUser]);

    const handleBiometricChange = (field, val) => {
        setBiometrics(prev => ({
            ...prev,
            [field]: val
        }));
    };

    useEffect(() => {
        setCurrentUser(athleteId ? visitedUser : user);
        setHasUnsavedChanges(false);
    }, [visitedUser, user, athleteId]);

    useEffect(() => {
        if (!currentUser?._id) return;
        const fetchStats = async () => {
            try {
                const { data } = await API.getWorkoutStatsByUserId(currentUser._id);
                setAthleteStats(data);
            } catch (e) {
                setAthleteStats(null);
            }
        };
        fetchStats();
    }, [currentUser?._id]);

    if (currentUser) currentUser.targetTime = userTargetTimeTransform(currentUser.targetTime);

    const watchedHeight = Number(biometrics.height);
    const watchedWeight = Number(biometrics.weight);
    const watchedBirthYear = Number(biometrics.birthYear);

    const watchedAge =
        watchedBirthYear && watchedBirthYear >= 1900
            ? new Date().getFullYear() - watchedBirthYear
            : null;

    const watchedBMP =
        watchedWeight && watchedHeight && watchedBirthYear && watchedAge !== null
            ? (10 * watchedWeight + 6.25 * watchedHeight - 5 * watchedAge + 5).toFixed(2)
            : 'Not set';

    const watchedBMI =
        watchedWeight && watchedHeight && watchedHeight !== 0
            ? (watchedWeight / ((watchedHeight / 100) * (watchedHeight / 100))).toFixed(2)
            : 'Not set';

    const watchedHrMax =
        watchedBirthYear && watchedBirthYear >= 1900 && watchedAge !== null
            ? 220 - watchedAge
            : 'Not set';

    const queueUserUpdate = userD => {
        setCurrentUser(userD);
        setHasUnsavedChanges(true);
    };

    const saveProfile = async () => {
        if (!currentUser?._id) return;
        setIsSavingProfile(true);
        let saveFeedback = getProfileSaveFeedback();
        try {
            const payload = {
                ...currentUser,
                targetTime: targetTimeTransform(currentUser.targetTime)
            };
            if (!payload.gender) {
                delete payload.gender;
            }
            if (user._id === currentUser._id) {
                const { data } = await API.updateMyself(payload);
                setUser({ ...user, ...data });
                setCurrentUser({ ...data, targetTime: userTargetTimeTransform(data.targetTime) });
            } else {
                const { data } = await API.updateUser(currentUser._id, payload);
                setCurrentUser({
                    ...stripSetupEmailStatus(data),
                    targetTime: userTargetTimeTransform(data.targetTime)
                });
                saveFeedback = getProfileSaveFeedback(data);
            }
            setHasUnsavedChanges(false);
            toast[saveFeedback.type](saveFeedback.message);
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Failed to save profile.');
        } finally {
            setIsSavingProfile(false);
        }
    };

    if (!currentUser) return;

    const paymentColumns = [
        {
            Header: 'Transaction ID',
            accessor: 'Transaction Id'
        },
        { Header: 'Payment Date', accessor: 'date' },
        {
            Header: 'Amount (€)',
            accessor: 'Amount'
        },
        {
            Header: 'Payment Status',
            accessor: 'Status',
            Cell: ({ value }) => (
                <div>
                    <Badge pill bg='success'>
                        Paid
                    </Badge>
                </div>
            )
        }
    ];

    return (
        <div className={styles.pageWrap}>
            <div className={styles.leftSide + ' island'}>
                <Avatar name={`${currentUser.firstName} ${currentUser.lastName}`} round={true} />
                <div className={styles.name}>
                    {currentUser.title} {currentUser.firstName} {currentUser.lastName}
                </div>
                <div className={styles.email}>{currentUser.email}</div>
                {
                    <div title='Location'>
                        <FaLocationDot style={{ color: 'red' }} />{' '}
                        {currentUser?.location ?? 'Not Provided'}
                    </div>
                }
                {
                    <div title='Swimming Club'>
                        <FaBuildingColumns style={{ color: 'var(--primary-color)' }} />{' '}
                        {currentUser.swimmingClub ?? 'Not Provided'}
                    </div>
                }
                <div className={styles.about}>
                    <div className={styles.aboutTitle}>About</div>
                    <div className={styles.aboutCntent}>
                        <b>Age:</b> {watchedAge}
                        <b>BMP:</b> {watchedBMP}
                        <b>BMI:</b> {watchedBMI}
                    </div>
                    <Badge pill bg='primary'>
                        Total Workouts Completed: {athleteStats?.workouts?.Workout?.Completed ?? 0}
                    </Badge>
                    <Badge pill className={styles.racesBadge}>
                        Total Races: {athleteStats?.workouts?.Race?.Completed ?? 0}
                    </Badge>
                    <Badge pill className={styles.testsBadge}>
                        Total Tests: {athleteStats?.tests?.total ?? 0}
                    </Badge>
                    <div
                        className={styles.aboutCntent}
                        title={currentUser.currentCSS || 'No CSS has been recorded for the user'}
                    >
                        <b>Critical Swim Speed (CSS):</b>{' '}
                        {currentUser?.currentCSS?.toFixed(2) === undefined
                            ? 'N/A'
                            : currentUser?.currentCSS?.toFixed(2)}
                    </div>
                    <div className={styles.aboutCntent}>
                        <span>
                            <b>Maximum Swim Speed (MSS):</b>{' '}
                            {currentUser?.currentMSS?.toFixed(2) === undefined
                                ? 'N/A'
                                : currentUser?.currentMSS?.toFixed(2)}
                        </span>
                    </div>
                </div>
            </div>
            <div className={styles.rightSide}>
                <div className={styles.topBar}>
                    <div className={styles.viewToggle + ' island'}>
                        <button
                            className={`${styles.toggleBtn} ${
                                viewMode === 'general' ? styles.active : ''
                            }`}
                            onClick={() => setViewMode('general')}
                            title='View General'
                        >
                            <TbChartCandle size={20} />
                            <span>General</span>
                        </button>
                        <button
                            className={`${styles.toggleBtn} ${
                                viewMode === 'security' ? styles.active : ''
                            }`}
                            onClick={() => setViewMode('security')}
                            title='View Security Settings'
                        >
                            <MdOutlineSecurity size={20} />
                            <span>Security</span>
                        </button>
                        <button
                            className={`${styles.toggleBtn} ${
                                viewMode === 'billing' ? styles.active : ''
                            }`}
                            onClick={() => setViewMode('billing')}
                            title='View Billing Information'
                        >
                            <FaCreditCard size={20} />
                            <span>Billing</span>
                        </button>
                        <button
                            className={`${styles.toggleBtn} ${
                                viewMode === 'notifications' ? styles.active : ''
                            }`}
                            onClick={() => setViewMode('notifications')}
                            title='View Notification Settings'
                        >
                            <IoNotifications size={20} />
                            <span>Notifications</span>
                        </button>
                    </div>
                </div>
                <div className={styles.selectedMenuItem}>
                    {viewMode === 'general' && (
                        <>
                            <Accordion
                                className={styles.accordion + ' island'}
                                defaultActiveKey={['0', '1', '2']}
                                alwaysOpen
                            >
                                <Accordion.Item eventKey='0'>
                                    <Accordion.Header>
                                        Basic Information
                                        <FaPassport style={{ marginLeft: '5px' }} size={20} />
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <Container fluid>
                                            <Row className={styles.infoRow}>
                                                <Col>
                                                    <b>Email: </b>
                                                    {(() => {
                                                        // The API only accepts the change from an admin or an associated coach
                                                        const canEditEmail =
                                                            athleteId &&
                                                            (user?.type === 'Admin' || user?.type === 'Coach');

                                                        return canEditEmail ? (
                                                            <EditableField
                                                                value={currentUser.email}
                                                                placeholder='Enter Email'
                                                                onSave={val => {
                                                                    queueUserUpdate({
                                                                        ...currentUser,
                                                                        email: val
                                                                    });
                                                                }}
                                                            />
                                                        ) : (
                                                            <span>{currentUser.email}</span>
                                                        );
                                                    })()}
                                                </Col>
                                                <Col>
                                                    <b>Phone Number:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.phoneNumber}
                                                        placeholder='Enter Phone Number'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                phoneNumber: val
                                                            });
                                                        }}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row className={styles.infoRow}>
                                                <Col>
                                                    <b>Title:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.title}
                                                        placeholder='Enter Title'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                title: val
                                                            });
                                                        }}
                                                    />
                                                </Col>
                                                <Col>
                                                    <b>First Name:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.firstName}
                                                        placeholder='Enter First Name'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                firstName: val
                                                            });
                                                        }}
                                                    />
                                                </Col>
                                                <Col>
                                                    <b>Last Name:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.lastName}
                                                        placeholder='Enter Last Name'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                lastName: val
                                                            });
                                                        }}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row className={styles.infoRow}>
                                                <Col>
                                                    <b>Birth Year:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.birthYear}
                                                        placeholder='Enter Birth Year'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                birthYear: val
                                                            });
                                                        }}
                                                    />
                                                </Col>
                                                <Col>
                                                    <b>Gender:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.gender}
                                                        placeholder='Select Gender'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                gender: val
                                                            });
                                                        }}
                                                        type='select'
                                                        options={[
                                                            { value: '', label: 'Select Gender' },
                                                            { value: 'Female', label: 'Female' },
                                                            { value: 'Male', label: 'Male' },
                                                            { value: 'Other', label: 'Other' }
                                                        ]}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row className={styles.infoRow}>
                                                <Col>
                                                    <b>Location:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.location}
                                                        placeholder='Enter Location'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                location: val
                                                            });
                                                        }}
                                                    />
                                                </Col>
                                                <Col>
                                                    <b>Swimming Club:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.swimmingClub}
                                                        placeholder='Enter Swimming Club'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                swimmingClub: val
                                                            });
                                                        }}
                                                    />
                                                </Col>
                                            </Row>
                                        </Container>
                                    </Accordion.Body>
                                </Accordion.Item>
                                <Accordion.Item eventKey='1'>
                                    <Accordion.Header>
                                        Athlete Information
                                        <HiOutlineIdentification
                                            style={{ marginLeft: '5px' }}
                                            size={20}
                                        ></HiOutlineIdentification>
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <Container fluid>
                                            <Row className={styles.infoRow}>
                                                <Col>
                                                    <b>Athlete Type:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.athleteType}
                                                        placeholder='Select Athlete Type'
                                                        onSave={async val => {
                                                            // If the type changes, clear preferredDistance
                                                            const shouldClearDistanceAndTime =
                                                                currentUser.athleteType &&
                                                                currentUser.athleteType !== val;
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                athleteType: val,
                                                                preferredDistance:
                                                                    shouldClearDistanceAndTime
                                                                        ? null
                                                                        : currentUser.preferredDistance,
                                                                targetTime:
                                                                    shouldClearDistanceAndTime
                                                                        ? null
                                                                        : currentUser.targetTime
                                                            });
                                                        }}
                                                        type='select'
                                                        options={[
                                                            { value: '', label: 'Select Type' },
                                                            { value: 'Pool', label: 'Pool' },
                                                            {
                                                                value: 'Open Water',
                                                                label: 'Open Water'
                                                            }
                                                        ]}
                                                    />
                                                </Col>
                                                <Col>
                                                    <b>Athlete Level:</b>{' '}
                                                    <EditableField
                                                        value={currentUser.athleteLevel}
                                                        placeholder='Select Athlete Level'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                athleteLevel: val
                                                            });
                                                        }}
                                                        type='select'
                                                        options={[
                                                            { value: '', label: 'Select Level' },
                                                            { value: 'Young', label: 'Young' },
                                                            {
                                                                value: 'Adolescent',
                                                                label: 'Adolescent'
                                                            },
                                                            { value: 'Elite', label: 'Elite' },
                                                            { value: 'Master', label: 'Master' }
                                                        ]}
                                                    />
                                                </Col>
                                            </Row>

                                            {/* Favorite Race for Pool */}
                                            {currentUser.athleteType === 'Pool' && (
                                                <>
                                                    <Row className={styles.infoRow}>
                                                        <b style={{ color: '#4097b7' }}>
                                                            Favourite Race:{' '}
                                                        </b>
                                                    </Row>
                                                    <Row className={styles.infoRow}>
                                                        <Col>
                                                            <b>Distance:</b>{' '}
                                                            <EditableField
                                                                value={
                                                                    currentUser.preferredDistance
                                                                }
                                                                placeholder='Select Distance'
                                                                onSave={async val => {
                                                                    queueUserUpdate({
                                                                        ...currentUser,
                                                                        preferredDistance: val
                                                                    });
                                                                }}
                                                                type='select'
                                                                options={[
                                                                    {
                                                                        value: '',
                                                                        label: 'Select Distance'
                                                                    },
                                                                    { value: '50', label: '50' },
                                                                    { value: '100', label: '100' },
                                                                    { value: '200', label: '200' },
                                                                    { value: '400', label: '400' },
                                                                    { value: '800', label: '800' },
                                                                    { value: '1500', label: '1500' }
                                                                ]}
                                                            />
                                                        </Col>
                                                        <Col>
                                                            <b>Style:</b>{' '}
                                                            <EditableField
                                                                value={currentUser.preferredStyle}
                                                                placeholder='Select Style'
                                                                onSave={val => {
                                                                    queueUserUpdate({
                                                                        ...currentUser,
                                                                        preferredStyle: val
                                                                    });
                                                                }}
                                                                type='select'
                                                                options={[
                                                                    {
                                                                        value: '',
                                                                        label: 'Select Style'
                                                                    },
                                                                    {
                                                                        value: 'Freestyle',
                                                                        label: 'Freestyle'
                                                                    },
                                                                    {
                                                                        value: 'Backstroke',
                                                                        label: 'Backstroke'
                                                                    },
                                                                    {
                                                                        value: 'BreastStroke',
                                                                        label: 'Breaststroke'
                                                                    },
                                                                    {
                                                                        value: 'Butterfly',
                                                                        label: 'Butterfly'
                                                                    },
                                                                    {
                                                                        value: 'IM',
                                                                        label: 'Individual Medley'
                                                                    }
                                                                ]}
                                                            />
                                                        </Col>
                                                        {/* Comment in when the new time picker is available */}
                                                        {/* <Col>
                                                                <b>Target Time:</b>{" "}
                                                                <EditableTimeField
                                                                    value={currentUser.targetTime}
                                                                    placeholder="Enter Target Time in mm:ss format"
                                                                    onSave={async (timeObj) => {
                                                                        await updateUser({ ...currentUser, targetTime: targetTimeTransform(timeObj) });
                                                                    }}
                                                                    includeHours={false}
                                                                    includeSeconds={true}
                                                                />
                                                            </Col> */}
                                                    </Row>
                                                </>
                                            )}
                                            {/* Favorite Race for Open Water */}
                                            {currentUser.athleteType === 'Open Water' && (
                                                <>
                                                    <Row className={styles.infoRow}>
                                                        <b style={{ color: '#4097b7' }}>
                                                            Favourite Race:{' '}
                                                        </b>
                                                    </Row>
                                                    <Row className={styles.infoRow}>
                                                        <Col>
                                                            <b>Distance:</b>{' '}
                                                            <EditableField
                                                                value={
                                                                    currentUser.preferredDistance
                                                                }
                                                                placeholder='Select Distance'
                                                                onSave={val => {
                                                                    queueUserUpdate({
                                                                        ...currentUser,
                                                                        preferredDistance: val
                                                                    });
                                                                }}
                                                                type='select'
                                                                options={[
                                                                    {
                                                                        value: '',
                                                                        label: 'Select Distance'
                                                                    },
                                                                    { value: '750', label: '750' },
                                                                    {
                                                                        value: '1500',
                                                                        label: '1500'
                                                                    },
                                                                    {
                                                                        value: '3000',
                                                                        label: '3000'
                                                                    },
                                                                    {
                                                                        value: '5000',
                                                                        label: '5000'
                                                                    },
                                                                    {
                                                                        value: '10000',
                                                                        label: '10000'
                                                                    },
                                                                    {
                                                                        value: '30000',
                                                                        label: '30000'
                                                                    }
                                                                ]}
                                                            />
                                                        </Col>
                                                        {/* Comment in when the new time picker is available */}
                                                        {/* <Col>
                                                                <b>Target Time:</b>{" "}
                                                                <EditableField
                                                                    value={
                                                                        currentUser.targetTime
                                                                            ? `${currentUser.targetTime.minutes?.toString().padStart(2, '0')}:${currentUser.targetTime.seconds?.toString().padStart(2, '0')}`
                                                                            : ""
                                                                    }
                                                                    placeholder="Enter Target Time (mm:ss)"
                                                                    onSave={async (val) => {
                                                                        const [minutes, seconds] = val.split(":").map(Number);
                                                                        const timeObj = { minutes, seconds };
                                                                        await updateUser({ ...currentUser, targetTime: targetTimeTransform(timeObj) });
                                                                    }}
                                                                    type="text"
                                                                />
                                                            </Col> */}
                                                    </Row>
                                                </>
                                            )}
                                        </Container>
                                    </Accordion.Body>
                                </Accordion.Item>
                                <Accordion.Item eventKey='2'>
                                    <Accordion.Header>
                                        Biometrics
                                        <FaPersonCircleQuestion
                                            style={{ marginLeft: '5px' }}
                                            size={20}
                                        />
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <Container fluid>
                                            <Row className={styles.infoRow}>
                                                <Col>
                                                    <b>Height (cm):</b>{' '}
                                                    <EditableField
                                                        value={currentUser.height}
                                                        placeholder='Enter Height'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                height: val
                                                            });
                                                        }}
                                                        onChange={val =>
                                                            handleBiometricChange('height', val)
                                                        }
                                                        type='number'
                                                    />
                                                </Col>
                                                <Col>
                                                    <b>Weight (kg):</b>{' '}
                                                    <EditableField
                                                        value={currentUser.weight}
                                                        placeholder='Enter Weight'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                weight: val
                                                            });
                                                        }}
                                                        onChange={val =>
                                                            handleBiometricChange('weight', val)
                                                        }
                                                        type='number'
                                                    />
                                                </Col>
                                            </Row>
                                            <Row className={styles.infoRow}>
                                                <Col>
                                                    <b>Sitting Height (cm):</b>{' '}
                                                    <EditableField
                                                        value={currentUser.sittingHeight}
                                                        placeholder='Enter Sitting Height'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                sittingHeight: val
                                                            });
                                                        }}
                                                        type='number'
                                                    />
                                                </Col>
                                                <Col>
                                                    <b>Hand opening (cm):</b>{' '}
                                                    <EditableField
                                                        value={currentUser.handOpening}
                                                        placeholder='Enter Hand Opening'
                                                        onSave={val => {
                                                            queueUserUpdate({
                                                                ...currentUser,
                                                                handOpening: val
                                                            });
                                                        }}
                                                        type='number'
                                                    />
                                                </Col>
                                            </Row>
                                            <Row className={styles.infoRow}>
                                                <Col>
                                                    <b>BMP (Basic Metabolic Panel):</b>{' '}
                                                    <span>{watchedBMP}</span>
                                                </Col>
                                                <Col>
                                                    <b>BMI (Body Mass Index):</b>{' '}
                                                    <span>{watchedBMI}</span>
                                                </Col>
                                                <Col>
                                                    <b>Hr max:</b> <span>{watchedHrMax}</span>
                                                </Col>
                                            </Row>
                                        </Container>
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>
                        </>
                    )}
                    {viewMode === 'security' && (
                        <div className={styles.securityPage}>
                            <div className={styles.changePasswordArea + ' island'}>
                                <div className={styles.header}>Change Password</div>
                                <form
                                    className={styles.changePasswordContent + ' form-group'}
                                    onSubmit={handlePasswordSubmit(onPasswordChange)}
                                >
                                    <div className={styles.changePasswordField}>
                                        <label htmlFor='currentPassword'>Current Password</label>
                                        <input
                                            type='password'
                                            className='form-control'
                                            id='currentPassword'
                                            placeholder='Enter current password'
                                            {...registerPassword('currentPassword', {
                                                required: true
                                            })}
                                        />
                                        {passwordErrors.currentPassword && (
                                            <span className={styles.error}>
                                                Current password is required
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.changePasswordField}>
                                        <label htmlFor='newPassword'>New Password</label>
                                        <input
                                            type='password'
                                            className='form-control'
                                            id='newPassword'
                                            placeholder='Enter new password'
                                            {...registerPassword('newPassword', {
                                                required: true,
                                                minLength: 5
                                            })}
                                        />
                                        {passwordErrors.newPassword && (
                                            <span className={styles.error}>
                                                New password is required (min 6 chars)
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.changePasswordField}>
                                        <label htmlFor='confirmPassword'>
                                            Confirm New Password
                                        </label>
                                        <input
                                            type='password'
                                            className='form-control'
                                            id='confirmPassword'
                                            placeholder='Confirm new password'
                                            {...registerPassword('confirmPassword', {
                                                required: true
                                            })}
                                        />
                                        {passwordErrors.confirmPassword && (
                                            <span className={styles.error}>
                                                Please confirm your new password
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        className={`${styles.changePasswordFormButton} btn btn-primary`}
                                        type='submit'
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Submitting...' : 'Submit'}
                                    </button>
                                </form>
                            </div>
                            <div className={styles.disableAccountArea + ' island'}>
                                <div className={styles.header}>Disable Account</div>
                                <div className={styles.disableAccountContent}>
                                    <div>
                                        Disable your account is a non permanent action and can only
                                        be undone by support. If you are sure you want to disable
                                        your account, select the button below.
                                    </div>
                                    <Button
                                        variant='outline-danger'
                                        onClick={() =>
                                            toast.error(
                                                'Feature temporarily disabled. Please contact support.'
                                            )
                                        }
                                    >
                                        I understand, disable my account
                                    </Button>{' '}
                                </div>
                            </div>
                        </div>
                    )}
                    {viewMode === 'billing' && (
                        <div className={styles.billingPage}>
                            <div className={styles.topCards}>
                                <div
                                    className={styles.billingCard + ' island'}
                                    style={{ borderLeft: '5px solid var(--base-color)' }}
                                >
                                    <small>Current monthly bill</small>
                                    <span className={styles.billingCardSpan}>0€</span>
                                </div>
                                <div
                                    className={styles.billingCard + ' island'}
                                    style={{ borderLeft: '5px solid var(--primary-mss-color)' }}
                                >
                                    <small>Next payment due</small>
                                    <span className={styles.billingCardSpan}>N/A</span>
                                </div>
                                <div
                                    className={styles.billingCard + ' island'}
                                    style={{ borderLeft: '5px solid #4cd964' }}
                                >
                                    <small>Current plan</small>
                                    <span className={styles.billingCardSpan}>Free</span>
                                </div>
                            </div>
                            <div className={styles.paymentMethods}>
                                <Accordion
                                    className={styles.accordion + ' island'}
                                    defaultActiveKey={['0', '1', '2']}
                                    alwaysOpen
                                >
                                    <Accordion.Item eventKey='0'>
                                        <Accordion.Header>Payment Methods</Accordion.Header>
                                        <Accordion.Body>
                                            <div className={styles.paymentMethodsArea}>
                                                <div className={styles.paymentMethodRow}>
                                                    <span>
                                                        <BsCashCoin
                                                            size={17}
                                                            style={{ marginRight: '5px' }}
                                                        ></BsCashCoin>{' '}
                                                        Cash
                                                    </span>
                                                    <Badge
                                                        pill
                                                        style={{ fontSize: '13px' }}
                                                        bg='secondary'
                                                    >
                                                        Disabled
                                                    </Badge>{' '}
                                                </div>
                                                <div className={styles.paymentMethodRow}>
                                                    <span>
                                                        <BsBank
                                                            size={17}
                                                            style={{ marginRight: '5px' }}
                                                        />
                                                        Bank Transfer
                                                    </span>
                                                    <Badge
                                                        pill
                                                        style={{ fontSize: '13px' }}
                                                        bg='secondary'
                                                    >
                                                        Disabled
                                                    </Badge>
                                                </div>
                                                <div className={styles.paymentMethodRow}>
                                                    <span>
                                                        <FaRegCreditCard
                                                            size={17}
                                                            style={{ marginRight: '5px' }}
                                                        />
                                                        Debit Card
                                                    </span>
                                                    <Badge
                                                        pill
                                                        style={{ fontSize: '13px' }}
                                                        bg='secondary'
                                                    >
                                                        Disabled
                                                    </Badge>{' '}
                                                </div>
                                            </div>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                    <Accordion.Item eventKey='1'>
                                        <Accordion.Header>Payment History</Accordion.Header>
                                        <Accordion.Body>
                                            <Table
                                                columns={paymentColumns}
                                                data={[
                                                    {
                                                        'Transaction Id': '#000',
                                                        date: '-',
                                                        Amount: '0.00',
                                                        Status: 'Paid'
                                                    }
                                                ]}
                                            ></Table>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            </div>
                        </div>
                    )}
                    {viewMode === 'notifications' && (
                        <div className={styles.notificationsPage}>
                            <div className={styles.emailNotificationsArea + ' island'}>
                                <div className={styles.emailNotificationsHeader}>
                                    Email Notifications
                                </div>
                                <div className={styles.emailNotificationsContent}>
                                    <div className={styles.emailNotificationField}>
                                        <label
                                            className={styles.notificationFieldLabel}
                                            for='currentEmail'
                                        >
                                            Default notification Email
                                        </label>
                                        <input
                                            type='button'
                                            disabled={true}
                                            id='currentEmail'
                                            value={currentUser.email}
                                        ></input>
                                    </div>
                                    <form onSubmit={handleSubmit(onNotificationFormSubmit)}>
                                        <div className={styles.emailNotificationField}>
                                            <label
                                                className={styles.notificationFieldLabel}
                                                for='changesToAccount'
                                            >
                                                Changes made to your account
                                            </label>
                                            <input
                                                type='checkbox'
                                                id='changesToAccount'
                                                {...register('changesToAccount')}
                                                defaultChecked={
                                                    currentUser.notifications?.changesToAccount ??
                                                    false
                                                }
                                            />{' '}
                                        </div>
                                        <div className={styles.emailNotificationField}>
                                            <label
                                                className={styles.notificationFieldLabel}
                                                for='promotionalOffers'
                                            >
                                                Marketing and Promotional Offers
                                            </label>
                                            <input
                                                type='checkbox'
                                                id='promotionalOffers'
                                                {...register('promotionalOffers')}
                                                defaultChecked={
                                                    currentUser.notifications?.promotionalOffers ??
                                                    false
                                                }
                                            />
                                        </div>
                                        <button
                                            className={`${styles.changePasswordFormButton} btn btn-primary`}
                                        >
                                            Submit
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        className='btn btn-primary'
                        onClick={saveProfile}
                        disabled={!hasUnsavedChanges || isSavingProfile}
                    >
                        {isSavingProfile ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
}
